import { AlertCircle, Folder } from "lucide-react";
import { useCallback, useMemo } from "react";
import type { MouseEvent } from "react";

import { NotionEditor } from "@/shared/components/editor";
import { useImageLightbox } from "@/shared/components/image-lightbox-context";

import { getItemTagName } from "../lib";
import type { Item } from "../types";
import type { TextPreviewContentState } from "./use-text-preview-content";

function formatTextPreviewDate(value?: string) {
  const date = value ? new Date(value) : new Date();

  if (Number.isNaN(date.getTime())) {
    return value ?? "";
  }

  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function TextPreview({
  contentState,
  folderName,
  item,
}: {
  contentState: TextPreviewContentState;
  folderName: string;
  item: Item;
}) {
  const { openImageLightbox } = useImageLightbox();
  const title = item.title || "标题";
  const createdAtLabel = useMemo(
    () => formatTextPreviewDate(item.created_at),
    [item.created_at],
  );
  const { content, contentMode, errorMessage } = contentState;
  const handleContentClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const clickedImage = event.target;

    if (
      !(clickedImage instanceof HTMLImageElement) ||
      !clickedImage.closest(".tiptap") ||
      clickedImage.closest('[data-type="emoji"]')
    ) {
      return;
    }

    const imageElements = Array.from(
      event.currentTarget.querySelectorAll<HTMLImageElement>(".tiptap img"),
    ).filter((imageElement) => !imageElement.closest('[data-type="emoji"]'));
    const clickedIndex = imageElements.indexOf(clickedImage);

    if (clickedIndex < 0) {
      return;
    }

    const images = imageElements.flatMap((imageElement, index) => {
      const src = imageElement.currentSrc || imageElement.src;

      return src
        ? [{
            alt: imageElement.alt || `${title} - 图片 ${index + 1}`,
            id: `${item.id}-${index}`,
            src,
          }]
        : [];
    });
    const initialImageId = `${item.id}-${clickedIndex}`;

    if (!images.some((image) => image.id === initialImageId)) {
      return;
    }

    openImageLightbox(images, initialImageId);
  }, [item.id, openImageLightbox, title]);

  return (
    <div className="flex min-h-full flex-col bg-background">
      <div className="mx-auto w-full max-w-4xl shrink-0 px-5 pt-8 pb-5 sm:px-10 lg:px-16">
        <h1 className="text-3xl font-bold leading-tight tracking-normal text-foreground">
          {title}
        </h1>

        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span>{createdAtLabel}</span>
          <span aria-hidden="true">•</span>
          <span className="inline-flex h-7 items-center gap-1.5 rounded-md bg-muted px-2 text-xs text-muted-foreground">
            <Folder className="size-3.5" />
            {folderName || "未分类"}
          </span>
          {item.tags?.length ? (
            <>
              <span aria-hidden="true">•</span>
              <div className="flex min-h-7 max-w-full flex-wrap items-center gap-1.5 text-xs text-foreground">
                {item.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex h-7 items-center rounded-md bg-muted px-2 text-xs text-muted-foreground"
                  >
                    #{getItemTagName(tag)}
                  </span>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <div
          className="mx-auto max-w-4xl pb-4 [&_.tiptap]:min-h-[360px] [&_.tiptap]:py-0 [&_.tiptap_img]:cursor-zoom-in [&_.tiptap_[data-type=emoji]_img]:cursor-auto [&>div]:border-0 [&>div]:shadow-none"
          onClick={handleContentClick}
        >
          {errorMessage ? (
            <div className="mx-5 mb-4 flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive sm:mx-10 lg:mx-16">
              <AlertCircle className="size-4" />
              {errorMessage}
            </div>
          ) : null}
          <NotionEditor
            contentMode={contentMode}
            initialContent={content}
            readOnly
          />
        </div>
      </div>
    </div>
  );
}
